<template>
  <section class="add-purchase">
    <h3 class="add-purchase__title">
      {{ spellTransactionType(transaction, user) }}
    </h3>
    <section class="image-and-form">
      <image-upload
        @loadStart="imageUrl = null"
        @loadImage="loadImage"
        @imageError="handleError"
        :value="imageUrl"
        size="43px"
      />
      <form @submit.prevent="submitTransaction" class="add-purchase-form">
        <div class="input-group_name">
          <!--          $t('CP.newTransaction')-->
          <app-input-small
            :default-text="cloneOperationInfo && cloneOperationInfo.name || ''"
            v-model="$v.name.$model"
            :error="$v.name.$error"
            @input="chooseError"
            :label="$t('CP.purpose')"
            :focus-on-mount="true"
          />
        </div>
        <div class="input-group_payee">
          <app-select-small
            :label="operationType === 'purchase' ? $t('CP.whoPays') : $t('CP.whoReceives')"
            :default="(transaction !== 'they purchased me' || cloneOperationInfo)
              ? (cloneOperationInfo && $getUserName(getContactByPhone(cloneOperationInfo.payee)))
              || $t('CP.me')
              : (contactsSelectInputs && contactsSelectInputs.keys && contactsSelectInputs.keys[1])
              || $t('CP.me')"
            class="transaction-select"
            @changeSelect="(input) => payee = input"
            :indexes="contactsSelectInputs.indexes"
            :keys="contactsSelectInputs.keys"
            :values="contactsSelectInputs.values"
          />
        </div>
        <div class="input-group_money">
          <div
            v-if="transaction !== 'split purchase'"
            class="transaction-input_money">
            <app-input-small
            :label="$t('CP.amount')"
              :default-text="cloneOperationInfo && cloneOperationInfo.amount.toString() || ''"
              v-model="$v.evenTransactionSum.$model"
              :error="$v.evenTransactionSum.$error"
              @input="chooseError"
              type="number"
              to-fixed-two
              placeholder="0.00"
            />
          </div>
          <app-select-small
            :label="$t('CP.currency')"
            :default="cloneOperationInfo && cloneOperationInfo.currentCurrency
              || eventCurrency || user.currency || ''"
            class="select-currency transaction-select"
            @changeSelect="(input) => currentCurrency = input"
            :indexes="currencySelectInputs.indexes"
            :keys="currencySelectInputs.keys"
            :values="currencySelectInputs.values"
            :groups="currencySelectInputs.groups"
            searchable
          />
        </div>

      </form>
    </section>
    <div class="invite-unregistered">
      <b-form-checkbox
        v-if="isInviteAllVisible"
        v-model="inviteAll"
      >
        {{ $t('CP.inviteUnregistered') }}
      </b-form-checkbox>
    </div>
    <participants-form
      class="participants-form"
      @toggleChangeParticipants="toggleChangeParticipants"
      title=""
      :even-for-rest-total="userMoney"
      :disable-adding="disableAddingPeople"
      :disable-deleting="disableDeletingPeople"
    />
    <div v-if="transaction === 'split purchase'" class="advanced-split-menu">
      <add-human-button
        @clicked="advancedOptionsOpened = !advancedOptionsOpened"
        :text="$t('CP.advancedOptions')"
        variant="settings"
        class="advanced-options-button"
      />
      <transition name="advancedOptions">
        <div v-show="advancedOptionsOpened" class="advanced-split-menu__input-group"
             :class="{'advanced-split-menu__input-group-proportional': isProportionalSplit}">
          <div
            class="advanced-split-option-container sum-total"
          >
            <app-input-small
              :label="isProportionalSplit ? $t('CP.sumTotalMessageSplitProportional')
              : $t('CP.sumTotalMessage')"
              v-model="$v.sumTotal.$model"
              type="number"
              text-align="left"
              to-fixed-two
              :default-text="sumTotal ? sumTotal.toString() : ''"
            />
          </div>
          <div class="advanced-split-option-container switch-proportional-split">
            <b-form-checkbox v-model="isProportionalSplit" switch>
              {{ $t('CP.proportionalRemainderSplit') }}
            </b-form-checkbox>
          </div>
          <div
            class="advanced-split-option-container tips"
          >
            <app-input-small
              :label="$t('CP.aTip')"
              v-model="tips"
              type="number"
              to-fixed-two
              :default-text="tips ? tips.toString() : ''"
            />
          </div>
          <div
            class="advanced-split-option-container service-fee"
          >
            <app-input-small
              :label="$t('CP.serviceFee')"
              v-model="serviceFee"
              type="number"
              to-fixed-two
              :default-text="serviceFee ? serviceFee.toString() : ''"
            />
            <span
              class="percentage"
            >%</span>
          </div>
        </div>
      </transition>
      <p class="transaction-sum transaction-sum-header">
        {{transaction === 'split purchase' ? $t('CP.totalAmount') : $t('CP.amount') }}:
      </p>
      <p class="transaction-sum">
        <span
          v-if="sumTotal"
        >
          {{ `${sumTotal.toFixed(2)} (${advancedSumTotal.toFixed(2)})` }}
        </span>
        <span
          v-else-if="splitTransactionSum"
        >
          {{ splitTransactionSum || 0 | toTwoFixed }}
        </span>
        <span
          v-else-if="sumTotal === '' || sumTotal === 0 && splitTransactionSum === 0"
        >
          0.00
        </span>
        <span
          style="margin-left: 4px;"
        >
          {{ currentCurrency }}
        </span>
      </p>
    </div>
    <span
      v-if="imageError"
      class="invalid-message">
      {{imageErrorMessage}}
    </span>
    <span
      v-if="errorMessage"
      class="form__error-message">
      {{errorMessage}}
    </span>
    <div
      class="form-buttons"
    >
      <button-app
        class="form-button form-button_cancel"
        @clicked="cancelSubmit"
        color-scheme="gray"
        type="button">
        {{ $t('CP.cancel') }}
      </button-app>
      <button-app
        class="form-button form-button_save"
        color-scheme="blue"
        @clicked="submitTransaction"
        type="button"
      >
        {{ $t('CP.create') }}
      </button-app>
    </div>
  </section>
</template>

<script>
import { mapState } from 'vuex';
import { required, minValue } from 'vuelidate/lib/validators';
import {
  ADD_TRANSACTION_REQUEST,
  EVENT_PAYOUTS_REQUEST,
  FETCH_EVENT_EXPENSES_REQUEST,
  FETCH_EVENT_SUCCESS,
  // FETCH_EVENTS_REQUEST,
  FETCH_EVENT_REQUEST,
  CLEAR_CURRENT_EVENT,
} from '@/store/events/types';
import {
  SET_EVENT_SELECTED_CONTACTS,
  SET_FINAL_EVENT_SELECTED_CONTACTS,
} from '@/store/user/types';
import {
  SET_CLONE_OPERATION_INFO, SET_SPLIT_PURCHASE_INFO,
} from '@/store/operations/types';
import ButtonApp from '../../components/Button/ButtonApp';
import AppInputSmall from '../../components/AppInputSmall/AppInputSmall';
import ImageUpload from '../../components/ImageUpload/ImageUpload';
import AppSelectSmall from '../../components/AppSelectSmall/AppSelectSmall';
import AddHumanButton from '../../components/AddHumanButton/AddHumanButton';
import ParticipantsForm from '../ParticipantsForm/ParticipantsForm';
import functionsWithForms from '../../utils/mixins/functionsWithForms';

export default {
  name: 'AddPurchaseForm',
  components: {
    ParticipantsForm,
    AddHumanButton,
    AppSelectSmall,
    ImageUpload,
    AppInputSmall,
    ButtonApp,
  },
  extends: functionsWithForms,
  props: {
    operationType: {
      type: String,
      default: '',
    },
    spellTransactionType: {
      type: Function,
      default: () => '',
    },
  },
  validations: {
    name: {
      required,
    },
    evenTransactionSum: {
      required,
      minValue: minValue(0.1),
    },
    splitTransactionSum: {
      required,
      minValue: minValue(0.1),
    },
    sumTotal: {
      required,
      minValue: minValue(0.1),
    },
  },
  data() {
    return {
      name: '',
      imageUrl: '',
      evenTransactionSum: '',
      payee: '',
      inviteAll: false,
      currentCurrency: '',
      errorMessage: '',
      imageError: false,
      imageErrorMessage: '',
      cloned: false,
      advancedOptionsOpened: false,
      sumTotal: '',
      tips: '',
      serviceFee: '',
      showParticipants: false,
      isProportionalSplit: true,
    };
  },
  mounted() {
    if (this.cloneOperationInfo && this.cloneOperationInfo.name
      && this.cloneOperationInfo.payee && this.cloneOperationInfo.currentCurrency
      && this.cloneOperationInfo.amount) {
      this.payee = this.cloneOperationInfo.payee;
      this.name = this.cloneOperationInfo.name;
      this.currentCurrency = this.cloneOperationInfo.currentCurrency;
      this.evenTransactionSum = this.cloneOperationInfo.amount;
      this.cloned = true;
    } else {
      this.choosePayee();
      this.currentCurrency = this.eventCurrency || this.user.currency || '';
    }
    this.openChooseContacts();
    this.setInitialSplitUsersMoney();
  },
  watch: {
    splitPurchaseInfo() {
      this.chooseError();
    },
    finalEventSelectedContacts() {
      this.chooseError();
    },
  },
  computed: {
    ...mapState({
      locale: (state) => state.app.currentLanguage,
      user: (state) => state.user.user,
      event: (state) => state.events.event,
      eventCurrency: (state) => state.events.eventCurrency,
      currencies: (state) => state.currency.currencies,
      transaction: (state) => state.operations.selectedOperation,
      splitPurchaseInfo: (state) => state.operations.purchaseInfo,
      cloneOperationInfo: (state) => state.operations.cloneOperationInfo,
      finalEventSelectedContacts: (state) => {
        if (state.operations.selectedOperation === 'i purchased them') {
          return state.user.finalEventSelectedContacts
            .filter((contact) => contact.uid !== state.user.user.user);
        }
        return state.user.finalEventSelectedContacts;
      },
    }),
    isInviteAllVisible() {
      return this.finalEventSelectedContacts
        .some((finalUser) => !finalUser.is_already_invited);
    },
    splitTransactionSum() {
      return (this.splitPurchaseInfo?.transactionSum) || 0;
    },
    splitUsersMoney() {
      return (this.splitPurchaseInfo && this.splitPurchaseInfo?.splitUsersMoney) || [];
    },
    userMoney() {
      return +((this.evenTransactionSum / (this.finalEventSelectedContacts.length || 1)) || 0)
        .toFixed(2);
    },
    isEventQuasi() {
      return !!this.event.quasi_event_name;
    },
    disableAddingPeople() {
      if (!this.isEventQuasi) return false;
      if (this.transaction === 'even purchase'
        || this.transaction === 'split purchase'
        || this.transaction === 'deposit') {
        return true;
      }
      if (this.transaction === 'i purchased them') {
        return false;
      }
      return false;
    },
    disableDeletingPeople() {
      if (!this.isEventQuasi) return false;
      if (this.transaction === 'even purchase'
        || this.transaction === 'split purchase'
        || this.transaction === 'deposit') {
        return true;
      }
      if (this.transaction === 'i purchased them') {
        return false;
      }
      return false;
    },
    advancedSumTotal() {
      if (typeof this.sumTotal !== 'undefined') {
        if (!this.sumTotal && !this.tips && !this.serviceFee) {
          return 0;
        }
        let total = this.sumTotal || 0;
        const splitTransactionSum = this.splitTransactionSum || 0;
        const serviceFee = this.serviceFee || 0;
        const tips = this.tips || 0;
        // old front formula
        total = (total - tips) / (1 + serviceFee / 100) - splitTransactionSum;
        // total -= ((tips) + ((total * serviceFee) / 100) + splitTransactionSum);
        return total;
      }
      return 0;
    },
    currencySelectInputs() {
      const usedCurrencies = this.event.used_currencies;
      const indexes = [];
      const keys = [];
      const values = [];
      const groupUsed = [];
      const groupOthers = [];
      this.currencies.forEach(({ id, currency }) => {
        indexes.push(id);
        keys.push(currency);
        values.push(currency);
        if (usedCurrencies?.includes(currency)) {
          groupUsed.push(currency);
        } else {
          groupOthers.push(currency);
        }
      });
      const inputs = {
        indexes,
        keys,
        values,
      };

      if (usedCurrencies?.length) {
        inputs.groups = [
          {
            name: this.$t('CP.currencyGroupUsed'),
            list: groupUsed,
          },
          {
            name: this.$t('CP.currencyGroupOthers'),
            list: groupOthers,
          },
        ];
      }

      return inputs;
    },
    contactsSelectInputs() {
      const indexes = [];
      const keys = [];
      const values = [];
      this.contactList.forEach((contact) => {
        indexes.push(contact.uid);
        keys.push(this.$getUserName(contact));
        values.push(contact.phone);
      });
      return {
        indexes,
        keys,
        values,
      };
    },
    contactList() {
      const result = [];
      if (this.event.contacts) {
        this.event.contacts.forEach((contact) => {
          if (contact.full_name === this.user.full_name) {
            result.unshift({
              ...contact,
              full_name: this.$t('CP.me'),
            });
          } else {
            result.push(contact);
          }
        });
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.payee = result[0].phone;
      }
      return result;
    },
  },
  methods: {
    handleError(message) {
      this.imageErrorMessage = message;
      this.imageError = !!message;
    },
    choosePayee() {
      if (this.transaction !== 'they purchased me') {
        this.payee = this.user.phone;
      } else {
        this.payee = (this.contactsSelectInputs && this.contactsSelectInputs.values
          && this.contactsSelectInputs.values[1])
          || this.user.phone;
      }
    },
    openChooseContacts() {
      if (this.transaction === 'i purchased them' && !this.finalEventSelectedContacts.length) {
        setTimeout(() => this.$bvModal.show('add-participants'), 300);
      }
    },
    toggleChangeParticipants() {
      this.$store.commit(SET_EVENT_SELECTED_CONTACTS, this.finalEventSelectedContacts);
      this.showParticipants = !this.showParticipants;
    },
    setInitialSplitUsersMoney() {
      if (this.transaction === 'split purchase') {
        const transactionSum = (this.splitPurchaseInfo && this.splitPurchaseInfo.transactionSum)
          || 0;
        const splitUsersMoney = (this.splitPurchaseInfo && this.splitPurchaseInfo.splitUsersMoney)
          || this.finalEventSelectedContacts.map((contact) => (
            {
              phone: contact.phone,
              money: 0,
            }
          ));
        this.$store.commit(SET_SPLIT_PURCHASE_INFO, {
          transactionSum,
          splitUsersMoney,
        });
      }
    },
    changeMoney(usersMoney) {
      this.splitUsersMoney = usersMoney;
      this.evenTransactionSum = usersMoney.reduce((sum, user) => {
        // eslint-disable-next-line no-param-reassign
        sum += +user.amount;
        return sum;
      }, 0);
    },
    chooseError() {
      if (this.$v.$dirty) {
        const payeeContact = this.getContactByPhone(this.payee);
        const minUsersInTransaction = this.finalEventSelectedContacts
          .filter((contact) => contact.uid !== payeeContact.uid)
          .length >= 1;
        if (this.transaction === 'even purchase' || this.transaction === 'deposit') {
          if (!minUsersInTransaction) {
            this.errorMessage = `${this.$t('validateText.moreThanOneParticipant')}!`;
          } else if (!this.$v.evenTransactionSum.required || !this.$v.name.required) {
            this.errorMessage = `${this.$t('validateText.fillInTheFields')}!`;
          } else if (!this.$v.evenTransactionSum.minValue) {
            this.errorMessage = `${this.$t('validateText.minimalAmount')} 0.1!`;
          } else {
            this.errorMessage = '';
          }
        } else if (this.transaction === 'i purchased them') {
          const anyUsersBesidesPayee = this.finalEventSelectedContacts
            .filter((user) => user.uid !== payeeContact.uid)
            .length >= 1;
          if (!anyUsersBesidesPayee) {
            this.errorMessage = `${this.$t('validateText.anyUsersBesidesPayee')}!`;
          } else if (!this.$v.evenTransactionSum.required || !this.$v.name.required) {
            this.errorMessage = `${this.$t('validateText.fillInTheFields')}!`;
          } else if (!this.$v.evenTransactionSum.minValue) {
            this.errorMessage = `${this.$t('validateText.minimalAmount')} 0.1!`;
          } else {
            this.errorMessage = '';
          }
        } else if (this.transaction === 'they purchased me') {
          const anyUsersBesidesPayee = this.finalEventSelectedContacts
            .filter((user) => user.uid !== payeeContact.uid)
            .length >= 1;
          if (!anyUsersBesidesPayee) {
            this.errorMessage = `${this.$t('validateText.anyUsersBesidesPayee')}!`;
          } else if (!this.$v.evenTransactionSum.required || !this.$v.name.required) {
            this.errorMessage = `${this.$t('validateText.fillInTheFields')}!`;
          } else if (!this.$v.evenTransactionSum.minValue) {
            this.errorMessage = `${this.$t('validateText.minimalAmount')} 0.1!`;
          } else {
            this.errorMessage = '';
          }
        } else if (this.transaction === 'split purchase') {
          const advancedSumTotal = this.advancedSumTotal || 0;
          const areThereUsersWhoDoesntPay = !(this.splitUsersMoney
            .every((user) => user.money >= 0.1));
          // eslint-disable-next-line no-empty
          if (advancedSumTotal < 0) {
            this.errorMessage = `${this.$t('validateText.sumRemainderNotNegative')}!`;
          } else if (!(advancedSumTotal < 0)) {
            if (this.finalEventSelectedContacts.length < 2) {
              this.errorMessage = `${this.$t('validateText.moreThanOneParticipant')}!`;
            } else if (areThereUsersWhoDoesntPay) {
              this.errorMessage = `${this.$t('validateText.allUsersAmountMoreThan')} 0.1!`;
            } else if (!this.$v.splitTransactionSum.minValue) {
              this.errorMessage = `${this.$t('validateText.minimalAmount')} 0.1!`;
            } else if (!this.$v.splitTransactionSum.required || !this.$v.name.required) {
              this.errorMessage = `${this.$t('validateText.fillInTheFields')}!`;
            } else {
              this.errorMessage = '';
            }
          }
        }
      } else {
        this.errorMessage = '';
      }
    },
    getContactById(id) {
      try {
        const inEventContacts = this.event.contacts.find((contact) => contact.uid === id);
        const inFriends = this.$store.state.user.userContacts.find((contact) => contact.uid === id);
        return inEventContacts || inFriends;
      } catch (e) {
        return '';
      }
    },
    getContactByPhone(phone) {
      try {
        const inEventContacts = this.event.contacts.find((contact) => contact.phone === phone);
        const inFriends = this.$store.state.user.userContacts
          .find((contact) => contact.phone === phone);
        if (inEventContacts && inEventContacts.uid === this.user.user) {
          inEventContacts.full_name = this.$t('CP.me');
        }
        if (inFriends && inFriends.uid === this.user.user) {
          inFriends.full_name = this.$t('CP.me');
        }
        return inEventContacts || inFriends;
      } catch (e) {
        return '';
      }
    },
    cancelSubmit() {
      this.$bvModal.hide('add-transaction');
      this.$store.commit(SET_CLONE_OPERATION_INFO, null);
      this.$store.commit(SET_SPLIT_PURCHASE_INFO, null);
      this.$store.commit(SET_EVENT_SELECTED_CONTACTS, []);
      this.$store.commit(SET_FINAL_EVENT_SELECTED_CONTACTS, []);
    },
    async submitTransaction() {
      this.$v.$touch();
      this.chooseError();
      if (this.transaction === 'even purchase'
        && (this.$v.evenTransactionSum.$anyError
          || this.$v.name.$anyError)) {
        return;
      }
      if (this.transaction === 'i purchased them'
        && (this.$v.evenTransactionSum.$anyError
          || this.$v.name.$anyError)) {
        return;
      }
      if (this.transaction === 'split purchase'
        && (this.$v.splitTransactionSum.$anyError
          || this.$v.name.$anyError)) {
        return;
      }
      if (this.transaction === 'deposit'
        && (this.$v.evenTransactionSum.$anyError
          || this.$v.name.$anyError)) {
        return;
      }
      if (this.errorMessage || this.imageError) {
        return;
      }
      const data = {
        details: this.name,
        currency: this.currentCurrency,
        event_id: this.event.id,
        payee: this.payee,
        invite_all: this.inviteAll,
        service_fee: this.serviceFee,
        tips: this.tips,
      };
      let imageUrl = '';
      let amount;
      if (this.imageUrl) {
        // eslint-disable-next-line prefer-destructuring
        imageUrl = this.imageUrl.split('base64,')[1];
      }
      let usersAmount = [];
      let purchaseType = '';
      if (this.transaction === 'split purchase') {
        purchaseType = 'split';
        amount = this.splitTransactionSum.toString();
        usersAmount = this.splitUsersMoney.filter((splitMoney) => splitMoney.money > 0);
        usersAmount = usersAmount
          .map((userAmount) => {
            let userMoney = userAmount.money;
            if (this.advancedSumTotal) {
              if (this.isProportionalSplit) {
                userMoney += parseFloat(
                  (userMoney / this.splitTransactionSum * this.advancedSumTotal).toFixed(10),
                );
              } else {
                userMoney += parseFloat((this.advancedSumTotal / usersAmount.length).toFixed(10));
              }
            }
            return {
              phone: userAmount.phone,
              amount: userMoney,
            };
          });
        if (this.sumTotal) {
          amount = this.sumTotal;
          data.even_for_rest_total = this.sumTotal;
        }
      } else {
        purchaseType = 'even';
        amount = this.evenTransactionSum.toString();
        this.finalEventSelectedContacts.forEach((contact) => {
          const { phone } = contact;
          usersAmount.push({
            phone,
            amount: this.userMoney,
          });
        });
      }
      data.image_url = imageUrl || null;
      data.participants = usersAmount;
      data.purchase_type = purchaseType;
      data.operation_type = this.operationType;
      data.amount = amount;
      this.$store.commit(SET_CLONE_OPERATION_INFO, null);
      const transactionData = await this.$store.dispatch(ADD_TRANSACTION_REQUEST, data);
      if (transactionData && transactionData.event_data) {
        this.$store.commit(FETCH_EVENT_SUCCESS, transactionData.event_data);
        this.$store.dispatch(FETCH_EVENT_EXPENSES_REQUEST, {
          ...transactionData.event_data,
          utcOffset: this.$moment().utcOffset(),
          currency: transactionData.currency,
          isUpdate: true,
        });
        this.$store.dispatch(EVENT_PAYOUTS_REQUEST, {
          ...transactionData.event_data,
          currency: transactionData.currency,
        });
      }
      // await this.$store.dispatch(FETCH_EVENTS_REQUEST);
      // this.$store.dispatch(FETCH_EVENT_REQUEST, {
      //   id: data.event_id,
      //   currency: data.currency,
      // });
      this.$store.commit(CLEAR_CURRENT_EVENT);
      this.$store.dispatch(FETCH_EVENT_REQUEST, {
        id: this.$route.params.id,
        utcOffset: this.$moment().utcOffset(),
        currency: this.$store.state.user.user.currency,
      });
      this.$bvModal.hide('add-transaction');
    },
  },
};
</script>

<style lang="scss" scoped>
@import "advancedOptionsTransition.css";
.advanced-options-button {
  width: 280px;
}
.add-purchase {
  color: var(--text-main-color);
}

.add-purchase__title {
  margin: 0 0 26px;
}

.image-and-form {
  display: flex;
  flex-direction: row;
}

.add-purchase-form {
  flex: 1 0 20%;
  display: flex;
  flex-wrap: wrap;
}

.input-group_name {
  flex: 1 0 100%;
}

.input-group_payee {
  flex: 0 1 45%;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
}

.input-group_money {
  flex: 0 1 45%;
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  margin-left: auto;
}

.transaction-input_money {
  max-width: 110px;
  margin: 0 8px 0 0;
}

.transaction-sum {
  display: flex;
  justify-content: flex-end;
  width: 40%;
  padding-right: 4px;
  text-align: right;
  margin: 0 0 0 auto;
  color: var(--text-main-color);
}

.transaction-sum-header {
  margin-top: 12px;
}

.invite-unregistered {
  flex: 1 0 100%;
  margin-top: 18px;
}

.split__footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 12px;
}

.advanced-split-menu {
  margin-top: -12px;
  .advanced-split-menu__input-group {
    display: flex;
    flex-wrap: wrap;
    height: fit-content;
    .sum-total {
      flex: 1 1 100%;
    }
    .tips {
      flex: 0 1 40%;
      margin-right: 5%;
      align-self: flex-end;
    }
    .switch-proportional-split {
      width: 100%;
      flex-shrink: 0;
      height: 48px;
      display: flex;
      align-items: flex-end;
    }
    .service-fee {
      display: flex;
      flex: 0 1 40%;
      align-self: flex-end;
    }
    .percentage {
      align-self: flex-end;
      font-size: 1.5rem;
    }
  }
}

.participants-form {
  padding: 12px;
  margin: 20px 0;
  border: 1px solid var(--gray-lighten);
  border-radius: 5px;
}

.form__error-message {
  margin: 0 auto 0 0;
  font-size: 1rem;
  color: var(--error-red);
  display: flex;
  align-items: flex-end;
}

.form-buttons {
  display: flex;
  flex: 1 0 50%;
  justify-content: flex-end;
  margin: 22px 0 0 0;
}

.form-button {
  width: 160px;
  height: 38px;
  margin: 0;
}

.form-button_cancel {
  margin: 0 8px 0 0;
}

.transaction-select {
  flex: 1;
}

@media screen and (max-width: $breakpoint-sm) {
  .input-group_payee {
    flex: 1 0 100%;
  }

  .input-group_money {
    flex: 1 0 100%;
  }
  .transaction-input_money {
    flex: 1;
    max-width: none;
  }
  .select-currency {
    height: 50px;
  }
  .transaction-sum {
    margin-bottom: 2px;
  }
  .image-and-form {
    flex-direction: column;
    align-items: center;
  }
  .form-buttons {
    justify-content: center;
    left: 16px;
    right: 16px;
    bottom: 4svh;
  }
}
</style>
